import {
  FC,
  KeyboardEventHandler,
  ReactNode,
  Ref,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { ImageWrap, Loading, theme } from '@hhgtech/hhg-components'
import { Button, LoadingOverlay, Text } from '@hhgtech/hhg-components/mantine'
import { Search } from '@hhgtech/icons/core'
import { ClinicPin } from '@hhgtech/icons/other'
import {
  AutocompleteItem,
  Box,
  clsx,
  Flex,
  rem,
  ScrollArea,
} from '@mantine/core'
import { useIntl } from 'react-intl'

import { UpDoubleArrowSvg } from 'components/icons'
import DownDoubleArrowSvg from 'components/icons/DownDoubleArrowSvg'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { CareHospital } from 'interfaces/types'

import { DefaultPagination, DistricItem, PaginationType } from '.'
import { ClinicLocatorSearchParams } from './clinicLocator.helpers'
import { ClinicLocatorSidebarLocation } from './clinicLocatorSidebarLocation'
import { ClinicLocatorSidebarSearch } from './clinicLocatorSidebarSearch'
import { ClinicLocatorContext } from './context'
import useStyles from './index.styles'

export type ClinicLocatorSidebarProps = {
  color?: string
  loading?: boolean
  extendComponent?: ReactNode
  clinics?: CareHospital[]
  clinicSelected?: CareHospital | null
  onSelectClinic?: (clinic: CareHospital) => void
  // Pagination
  pagination?: PaginationType
  onChange?: (key: string, value?: string | number | boolean) => void
  // Search Input
  searchLoading?: boolean
  searchValue?: string
  searchData?: CareHospital[]
  onChangeSearch?: (value: string) => void
  onSubmitSearch?: (value: string) => void
  onItemSubmitSearch?: (clinic: any) => void
  // Filter
  filter?: DistricItem[]
  onFilter?: (districtId: string) => void
  // Old Version
  searchRef?: Ref<HTMLInputElement>
  queryParams: ClinicLocatorSearchParams
  onUpdateDisplayClinics?: (clinics: CareHospital[]) => void
}

export const ClinicLocatorSidebar: FC<ClinicLocatorSidebarProps> = (props) => {
  const {
    color,
    loading: loadingProps,
    extendComponent,
    clinics,
    clinicSelected,
    onSelectClinic,
    pagination = DefaultPagination,
    searchLoading,
    searchData = [],
    searchValue,
    onChangeSearch,
    onSubmitSearch,
    onItemSubmitSearch,
    filter,
    onFilter,
    onChange: onChangeProps,
    searchRef,
  } = props
  const { isMobile } = useContext(ClinicLocatorContext)
  const { classes } = useStyles({ useMobileLayout: isMobile })
  const { locale } = useRouterExtend()
  const { formatMessage: f } = useIntl()
  const loadRef = useRef<HTMLDivElement>(null)
  const [search, setSearch] = useState<string>(searchValue || '')
  const [showList, setShowList] = useState(false)
  const [showSearch, setShowSearch] = useState(false)

  const loading = useMemo(() => (searchLoading ? false : loadingProps), [
    loadingProps,
    searchLoading,
  ])

  const { title: countTitle } = useMemo(
    () => ({
      title: f(
        { id: 'clinicLocator.foundClinics', defaultMessage: '{count} Clinics' },
        { count: pagination?.total_items.toLocaleString(locale) },
      ),
    }),
    [pagination?.total_items],
  )

  const isLoadMore = useMemo(() => pagination?.current < pagination?.last, [
    pagination?.current,
    pagination?.last,
  ])

  const InterObs = useMemo(
    () =>
      new IntersectionObserver(function (entries) {
        for (const entry of entries) {
          const { target, isIntersecting } = entry
          if (target === loadRef.current && isIntersecting && isLoadMore) {
            onChangeProps?.('page', pagination.current + 1)
          }
        }
      }),
    [onChangeProps, pagination?.current, isLoadMore],
  )

  const WrapperComponent = isMobile ? Flex : ScrollArea
  const wrapperComponentProps: any = isMobile
    ? { direction: 'column' }
    : {
        styles: { root: { height: '100%' }, thumb: { zIndex: 2 } },
        scrollbarSize: 6,
      }

  const handleSelectClinic = useCallback(
    (clinic: CareHospital) => {
      onSelectClinic?.(clinic)
    },
    [onSelectClinic],
  )

  const handleChangeSearch = useCallback(
    (val: string) => {
      if (val.includes('-----')) return
      setSearch(val)
      onChangeSearch?.(val)
    },
    [onChangeSearch],
  )

  const handleKeyUp = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    (e) => {
      if (e.key === 'Enter') {
        const value = (e.target as HTMLInputElement).value
        onSubmitSearch?.(value)
      }
    },
    [onSubmitSearch],
  )

  const handleOnItemSubmit = useCallback(
    (item: AutocompleteItem) => {
      const { value, label, ...clinic } = item
      onItemSubmitSearch?.(clinic)
    },
    [onItemSubmitSearch],
  )

  useEffect(() => {
    setSearch(searchValue || '')
  }, [searchValue])

  useEffect(() => {
    if (!loadRef.current) return
    InterObs.observe(loadRef.current as HTMLDivElement)
    return () => {
      InterObs.disconnect()
    }
  }, [InterObs])

  return (
    <WrapperComponent
      className="ClinicLocatorSidebar--wrapper"
      {...wrapperComponentProps}
    >
      <Flex
        align="stretch"
        gap={rem(16)}
        className={`ClinicLocatorSidebar--extends ${classes.sidebarExtend}`}
      >
        {extendComponent}
      </Flex>

      {/* Sticky Header */}
      <Box
        pos="sticky"
        top={0}
        left={0}
        right={0}
        bg={theme.colors.neutral50}
        className={clsx(
          'ClinicLocatorSidebar--sticky items-center relative',
          { 'pt-4': !isMobile },
          classes.sidebarSticky,
        )}
      >
        <Flex gap={rem(8)} align="center">
          <ClinicLocatorSidebarSearch
            ref={searchRef}
            data={(searchData || [])?.map((searchItem) => {
              const { id, name } = searchItem
              return { ...searchItem, value: `-----${id}`, label: name }
            })}
            loading={searchLoading && !loading}
            value={search}
            onChange={handleChangeSearch}
            onItemSubmit={handleOnItemSubmit}
            onKeyUp={handleKeyUp}
            onClickLeftIcon={() => setShowSearch(!showSearch)}
            className={clsx(
              'flex-1 min-w-0 transition-all duration-200',
              {
                'opacity-0 pointer-events-none': isMobile && !showSearch,
                'opacity-100 pointer-events-auto': isMobile && showSearch,
              },
              classes.sidebarSearch,
            )}
            sx={() => ({
              '.mantine-Input-icon': {
                pointerEvents: showSearch ? 'auto' : 'none',
              },
            })}
          />

          {/* Toggle list button */}
          <Button
            onClick={() => setShowList(!showList)}
            leftIcon={
              showList ? (
                <UpDoubleArrowSvg className="text-[24px] text-gray800" />
              ) : (
                <DownDoubleArrowSvg className="text-[24px] text-gray800" />
              )
            }
            className={clsx('ml-auto !w-6 !h-6 !p-0 !bg-[transparent]', {
              '!block': isMobile,
              '!hidden': !isMobile,
            })}
          />
        </Flex>

        {/* Search Header */}
        <Flex
          gap={rem(8)}
          align="center"
          className={clsx(
            'transition-all duration-200',
            {
              'absolute inset-0 z-[1] px-4 opacity-100 pointer-events-none':
                isMobile && !showSearch,
              'absolute inset-0 px-4 opacity-0 pointer-events-none':
                isMobile && showSearch,
            },
            classes.searchBtn,
          )}
        >
          {!isMobile && (
            <ClinicPin
              size={24}
              color={color || theme.colors.primary600}
              className="block"
            />
          )}

          {isMobile && (
            <Button
              leftIcon={<Search className="!text-white" />}
              className="!w-8 !h-8 !block pointer-events-auto"
              sx={() => ({
                backgroundColor: `${
                  color || theme.colors.primary600
                } !important`,
                svg: {
                  width: rem(18),
                  height: rem(18),
                  path: { fill: '#fff' },
                },
              })}
              onClick={() => setShowSearch(!showSearch)}
            />
          )}

          <Flex className={classes.countTitle}>
            <Text
              size="s3"
              fs={rem(16)}
              fw={600}
              lh={rem(24)}
              miw={0}
              sx={{ flex: 1 }}
            >
              {countTitle}
            </Text>
            <ClinicLocatorSidebarLocation
              data={(filter || [])?.map((f) => ({
                value: f.id,
                label: f.name,
              }))}
              onChange={onFilter}
              className="pointer-events-auto"
            />
          </Flex>
        </Flex>
      </Box>

      {/* Clinic List */}
      <Box
        className={clsx(
          'clinicLocatorSidebar--body',
          { '!hidden': isMobile && !showList },
          classes.sidebarList,
        )}
      >
        {loading && (
          <LoadingOverlay
            visible={loading}
            sx={() => ({ borderRadius: rem(20), border: 0 })}
          />
        )}

        {!loading &&
          clinics?.map((clinic, idx) => {
            const { id, logo, name, address } = clinic
            return (
              <Flex
                key={`${id}-${idx}`}
                gap={rem(16)}
                sx={() => ({ cursor: 'pointer' })}
                className={clsx('clinicLocatorSidebar--item relative', {
                  'min-w-[280px]': isMobile,
                  'p-3': isMobile,
                  'px-6 py-3': !isMobile,
                  'border-b border-solid border-gray100': !isMobile,
                  'bg-blue50': clinicSelected?.id === id && isMobile,
                  'bg-[transparent]': clinicSelected?.id === id && !isMobile,
                })}
                onClick={() => handleSelectClinic(clinic)}
              >
                <div
                  className={clsx(
                    'absolute bg-primary600 opacity-0 transform ease-linear',
                    {
                      'left-0 bottom-0 w-full h-[2px]': isMobile,
                      'top-0 left-[2px] w-[2px] h-full': !isMobile,
                      'opacity-100': clinicSelected?.id === id,
                    },
                  )}
                />
                {!isMobile && (
                  <Box className="w-8 h-8">
                    <ImageWrap
                      width="100%"
                      height="100%"
                      src={logo}
                      alt={name}
                      className="object-contain object-center"
                    />
                  </Box>
                )}
                <Box sx={() => ({ flex: 1, minWidth: 0 })}>
                  <Text size="s3" weight="semiBold">
                    {name}
                  </Text>
                  <Text size="p4" lineClamp={3}>
                    {address}
                  </Text>
                </Box>
              </Flex>
            )
          })}

        {isLoadMore && (
          <>
            {!searchLoading && !loadingProps && (
              <div ref={loadRef} className="clinicLocatorSidebar__loadmore" />
            )}
            {!loadingProps && <Loading />}
          </>
        )}
      </Box>
    </WrapperComponent>
  )
}
